import { useRef, useEffect, useCallback } from 'react';
import * as d3 from 'd3';

interface TooltipOptions {
  className?: string;
  offset?: { x: number; y: number };
  style?: {
    backgroundColor?: string;
    color?: string;
    padding?: string;
    borderRadius?: string;
    fontSize?: string;
    zIndex?: number;
    pointerEvents?: string;
    whiteSpace?: string;
  };
}

/**
 * D3.js用のツールチップを管理するカスタムフック
 * 
 * @param options ツールチップのオプション設定
 * @returns ツールチップの表示/非表示/移動/内容更新関数
 * 
 * @example
 * ```typescript
 * const tooltip = useD3Tooltip({ className: 'my-tooltip' });
 * 
 * // D3要素のイベントハンドラー内で使用
 * selection
 *   .on('mouseover', (event, d) => {
 *     tooltip.show(`Value: ${d.value}`, event);
 *   })
 *   .on('mousemove', (event) => {
 *     tooltip.move(event);
 *   })
 *   .on('mouseout', () => {
 *     tooltip.hide();
 *   });
 * ```
 */
export function useD3Tooltip(options: TooltipOptions = {}) {
  const tooltipRef = useRef<d3.Selection<HTMLDivElement, unknown, HTMLElement, undefined> | undefined>(undefined);
  
  const {
    className = 'd3-tooltip',
    offset = { x: 10, y: -10 },
    style = {}
  } = options;

  // デフォルトスタイルとユーザー指定スタイルをマージ
  const defaultStyle = {
    backgroundColor: 'rgba(0, 0, 0, 0.8)',
    color: 'white',
    padding: '8px',
    borderRadius: '4px',
    fontSize: '12px',
    zIndex: 1000,
    ...style
  };

  // ツールチップの初期化
  useEffect(() => {
    // 既存のツールチップを削除
    d3.select('body').selectAll(`.${className}`).remove();
    
    // 新しいツールチップを作成
    const tooltip = d3.select('body')
      .append('div')
      .attr('class', className)
      .style('position', 'absolute')
      .style('visibility', 'hidden')
      .style('pointer-events', 'none')
      .style('background-color', defaultStyle.backgroundColor)
      .style('color', defaultStyle.color)
      .style('padding', defaultStyle.padding)
      .style('border-radius', defaultStyle.borderRadius)
      .style('font-size', defaultStyle.fontSize)
      .style('z-index', String(defaultStyle.zIndex));
    
    tooltipRef.current = tooltip;

    // クリーンアップ
    return () => {
      tooltip.remove();
    };
  }, [className, defaultStyle.backgroundColor, defaultStyle.color, defaultStyle.padding, 
      defaultStyle.borderRadius, defaultStyle.fontSize, defaultStyle.zIndex]);

  // ツールチップを表示
  const show = useCallback((content: string, event?: MouseEvent) => {
    if (!tooltipRef.current) return;
    
    tooltipRef.current
      .style('visibility', 'visible')
      .html(content);
    
    if (event) {
      tooltipRef.current
        .style('left', `${event.pageX + offset.x}px`)
        .style('top', `${event.pageY + offset.y}px`);
    }
  }, [offset.x, offset.y]);

  // ツールチップを非表示
  const hide = useCallback(() => {
    if (!tooltipRef.current) return;
    
    tooltipRef.current.style('visibility', 'hidden');
  }, []);

  // ツールチップの位置を更新
  const move = useCallback((event: MouseEvent) => {
    if (!tooltipRef.current) return;
    
    tooltipRef.current
      .style('left', `${event.pageX + offset.x}px`)
      .style('top', `${event.pageY + offset.y}px`);
  }, [offset.x, offset.y]);

  // ツールチップの内容を更新（位置は変えない）
  const setContent = useCallback((content: string) => {
    if (!tooltipRef.current) return;
    
    tooltipRef.current.html(content);
  }, []);

  // トランジション付きの表示
  const showWithTransition = useCallback((content: string, event?: MouseEvent, duration = 200) => {
    if (!tooltipRef.current) return;
    
    tooltipRef.current
      .style('opacity', 0)
      .style('visibility', 'visible')
      .html(content);
    
    if (event) {
      tooltipRef.current
        .style('left', `${event.pageX + offset.x}px`)
        .style('top', `${event.pageY + offset.y}px`);
    }
    
    tooltipRef.current
      .transition()
      .duration(duration)
      .style('opacity', 1);
  }, [offset.x, offset.y]);

  // トランジション付きの非表示
  const hideWithTransition = useCallback((duration = 500) => {
    if (!tooltipRef.current) return;
    
    tooltipRef.current
      .transition()
      .duration(duration)
      .style('opacity', 0)
      .on('end', function(this: HTMLDivElement) {
        d3.select(this).style('visibility', 'hidden');
      });
  }, []);

  return {
    show,
    hide,
    move,
    setContent,
    showWithTransition,
    hideWithTransition,
    ref: tooltipRef
  };
}

// 戻り値の型をエクスポート
export type D3TooltipHook = ReturnType<typeof useD3Tooltip>;